import { incomingMessage } from "../../interfaces/messages.type";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { useEffect, useRef } from "react";
import { Conversation } from "../../interfaces/conversations.type";
import { fetchConversationsData } from "../apis/conversations.api";
import { AccountConfiguration, UserConfiguration } from "../../pages/Settings/Configurations";
import { Contact } from "@data-phone/react-generic/dist/components/Forms/Contacts";
import { setSelectedDid } from "../../store/slices/SmsDids.slice";
import { setSelectedConversation } from "../../store/slices/conversation.slice";
import { useNavigate } from "react-router-dom";
import LogoIcon from "../../assets/images/logo/Logomark Coloured.png";
import { maybeNotifyIncomingSmsDesktop } from "../../utils/incomingSmsDesktopNotification";
import { getConversationTitle } from "../../utils/conversationDisplay";

const useMessageNotification = () => {
    const dispatch: AppDispatch = useDispatch();
    const { data: conversations } = useSelector((state: RootState) => state.conversation);
    const { data: user } = useSelector((state: RootState) => state.sessionData);
    const { data: configurations } = useSelector((state: RootState) => state.configurations);
    const { data: contacts } = useSelector((state: RootState) => state.contacts);

    const navigate = useNavigate();

    const conversationsRef = useRef<Conversation[]>(conversations);
    const configurationsRef = useRef<{
        account: AccountConfiguration | null;
        user: UserConfiguration | null;
    }>(configurations);
    const contactsRef = useRef<Contact[]>(contacts);

    useEffect(() => {
        contactsRef.current = contacts;
    }, [contacts]);

    useEffect(() => {
        configurationsRef.current = configurations;
    }, [configurations]);

    useEffect(() => {
        conversationsRef.current = conversations;
    }, [conversations]);

    const getConversation = async (did: string, conversationId: string): Promise<Conversation | undefined> => {
        const conversation = await fetchConversationsData({ did, conversation_id: conversationId, limit: 1, offset: 0 }, 0, dispatch);

        if (conversation.length > 0) {
            return conversation[0];
        }

        return undefined;
    };

    const shouldPlaySound = (config: UserConfiguration, isAssignedCase: boolean, isUnassignedCase: boolean): boolean => {
        const { enabled, condition } = config.notifications.sound;

        if (!enabled) {
            return false;
        }

        if (condition === "always") {
            return true;
        }

        if (condition === "assigned" && isAssignedCase) {
            return true;
        }

        if (condition === "unassigned" && isUnassignedCase) {
            return true;
        }

        return false;
    };

    const shouldShowNotification = (config: UserConfiguration, isAssignedCase: boolean, isUnassignedCase: boolean): boolean => {
        const { enabled, condition } = config.notifications.popups;

        if (!enabled) {
            return false;
        }

        if (condition === "always") {
            return true;
        }

        if (condition === "assigned" && isAssignedCase) {
            return true;
        }

        if (condition === "unassigned" && isUnassignedCase) {
            return true;
        }

        return false;
    };

    const sendMessageNotification = async (message: incomingMessage) => {
        const participants = message.conversation_participants?.length
            ? message.conversation_participants
            : [message.participant];
        const title = getConversationTitle(participants, contactsRef.current);

        if (window.electronAPI?.notifyIncomingSms) {
            maybeNotifyIncomingSmsDesktop(message, title);
            return;
        }

        let conversation = conversationsRef.current.find(item => item.id === message.conversation_id);

        if (!conversation) {
            conversation = await getConversation(message.did, message.conversation_id);
        }

        if (!conversation) {
            return;
        }

        const isAssignedCase = conversation.assigned_user === user?.id;
        const isUnassignedCase = conversation.assigned_user === null;

        if (!configurationsRef.current.user) {
            return;
        }

        if (!shouldShowNotification(configurationsRef.current.user, isAssignedCase, isUnassignedCase)) {
            return;
        }

        const notificationPayload = {
            icon: LogoIcon,
            body: message.body,
            silent: !shouldPlaySound(configurationsRef.current.user, isAssignedCase, isUnassignedCase)
        };

        let notification: Notification | undefined;

        if (typeof Notification === "undefined") {
            return;
        }

        if (Notification.permission === "granted") {
            notification = new Notification(title, notificationPayload);
        } else if (Notification.permission !== "denied") {
            Notification.requestPermission().then(permission => {
                if (permission === "granted") {
                    notification = new Notification(title, notificationPayload);
                }
            });
        }

        if (notification) {
            notification.onclick = () => {
                window.focus();
                dispatch(setSelectedDid(message.did));
                dispatch(
                    setSelectedConversation({
                        conversation_id: message.conversation_id,
                        did: message.did,
                        participants,
                        type: message.conversation_type || (participants.length > 1 ? "group" : "direct"),
                    })
                );
                navigate("/");
                notification?.close();
            };
        }
    };

    return {
        sendMessageNotification
    };
};

export default useMessageNotification;
